
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { getYears } from '../../../../../utils/commonFunctions';
import { useEffect, useState } from 'react';

import dayjs from 'dayjs'
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

interface DatetimeInputProps {
    onChange: (value: number) => void; // epoch in seconds
    value: number; // epoch in seconds
    disabled?: boolean
}

/**
 * A datetime input component that allows users to select a datetime
 * in UTC timezone.
 *
 * @param {{ onChange: (value: number) => void, value: number, disabled?: boolean }} 
 * @returns {JSX.Element}
 */
const DatetimeInput: React.FC<DatetimeInputProps> = ({ onChange, value, disabled = false, id }: { onChange: (value: number) => void; value: number, disabled?: boolean, id?: string }): JSX.Element => {

    const [time, setTime] = useState<null | dayjs.Dayjs>(dayjs(value * 1000).utc())

    const onInputChange = (newValue: any) => {
        if (!newValue) {
            setTime(null);
            return
        }
        setTime(newValue)
        if (dayjs(newValue).isValid() && dayjs(newValue).unix() >= 0) {
            const epoch = dayjs(newValue).unix();
            onChange(epoch);
        }
    };

    useEffect(() => {
        setTime(dayjs(value * 1000).utc());
    }, [value, time])

    return (
        <div className={`d-flex align-items-center rounded-1 overflow-hidden w-100 p-0 ${disabled ? 'opacity-75 pointer-events-none cursor-auto' : ''}`}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                    disabled={disabled}
                    sx={{
                        borderRadius: '0px',
                        background: 'rgba(10, 10, 10, 0.65)!important',
                        '.MuiFormControl-root': {
                            paddingRight: '0px',
                        },
                        '.MuiInputBase-input': {
                            fontSize: '14px !important',
                        },
                        '.MuiOutlinedInput-input': {
                            fontSize: '14px !important',
                        },
                        '.MuiOutlinedInput-root': {
                            height: '30px !important',
                            paddingRight: '0px'
                        },
                        '.MuiInputAdornment-root': {
                            display: 'none',
                        },
                        '.MuiOutlinedInput-notchedOutline': {
                            borderRadius: '0px',
                            border: 'none',
                            borderRight: '0.5px solid rgba(240,240,240,0.30) !important',
                        },
                        '.Mui-disabled': {
                            color: 'rgba(240,240,240,0.30) !important',
                            '-webkit-text-fill-color ': 'rgba(240,240,240,0.30) !important',
                            cursor: 'auto'
                        }
                    }}
                    shouldDisableYear={(date) => {
                        const current_year = dayjs(date).year();
                        const yearList = getYears(2002, 2037);
                        return !yearList.includes(current_year);
                    }}
                    className="rounded-3 w-90"
                    ampm={false} format='YYYY-MM-DD HH:mm:ss'
                    views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                    value={time ?? null} onChange={onInputChange} />
            </LocalizationProvider>
            <span className='opacity-75 fs-12 px-2'>UTC</span>
        </div>
    )
}

export default DatetimeInput